import { useEffect, useState } from 'react';
import { useRedemption } from '../../hooks/useRedemption';
import { formatDiscount, expiryLabel, safeExternalUrl } from '../../utils/helpers';
import { Spinner } from '../common/UI';
import MerchantLogo from '../common/MerchantLogo';

export default function RedemptionModal({ coupon, onClose }) {
  const { redeem, loading, result, error, reset } = useRedemption();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (coupon) redeem(coupon.id);
  }, [coupon?.id]); // eslint-disable-line

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!coupon) return null;

  const handleClose = () => { reset(); onClose(); };

  const handleVisitMerchant = () => {
    // Try each candidate in turn — a malformed affiliate URL should fall
    // through to the merchant's own site rather than opening a dead tab.
    const url = [result?.affiliate_url, coupon.affiliate_tracking_url, coupon.merchant?.website]
      .map(safeExternalUrl)
      .find(Boolean);
    if (url) window.open(url, '_blank', 'noopener');
    handleClose();
  };

  const copyCode = () => {
    if (result?.code) {
      navigator.clipboard.writeText(result.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    }
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 520, overflow: 'hidden' }}>
        {/* Top gradient header */}
        <div style={{ background: 'var(--brand-gradient)', color: '#fff', padding: '28px 28px 24px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -50, right: -30, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.12)' }} />
          <div style={{ position: 'absolute', bottom: -60, left: -30, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <button onClick={handleClose} style={{ position: 'absolute', top: 14, right: 14, background: 'rgba(255,255,255,0.15)', border: 'none', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 18, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, zIndex: 2 }}>×</button>
          <div style={{ position: 'relative' }}>
            <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.9, marginBottom: 6 }}>Your Deal Is Ready</p>
            <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.25 }}>{coupon.title}</h2>
            <p style={{ fontSize: 14, opacity: 0.9, marginTop: 8 }}>
              <strong>{formatDiscount(coupon)}</strong> at {coupon.merchant?.name}
            </p>
          </div>
        </div>

        <div style={{ padding: 28 }}>
          {/* Merchant row */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 22, padding: '14px 16px', background: '#F7F8FC', borderRadius: 12, border: '1px solid #F0F2F8' }}>
            <MerchantLogo merchant={coupon.merchant} size={46} radius={10} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontWeight: 700, fontSize: 15 }}>{coupon.merchant?.name}</p>
              <p style={{ fontSize: 12, color: '#8B94A8', marginTop: 2 }}>{expiryLabel(coupon.expires_at)}</p>
            </div>
          </div>

          {loading && (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <Spinner size={36} />
              <p style={{ marginTop: 16, color: '#4A5568', fontSize: 14, fontWeight: 500 }}>Getting your code…</p>
            </div>
          )}

          {error && !loading && (
            <div className="alert alert-danger">{error}</div>
          )}

          {/* Code state */}
          {result?.code && !loading && (
            <div style={{ marginBottom: 22 }}>
              <p style={{ fontSize: 13, color: '#4A5568', marginBottom: 12, fontWeight: 500 }}>
                Copy this code and paste it at checkout:
              </p>
              <div style={{
                border: '2px dashed #6B2FD9', borderRadius: 14, padding: '4px',
                background: 'linear-gradient(135deg, #F3EEFF 0%, #FFE8D6 100%)',
                position: 'relative',
              }}>
                <div style={{ display: 'flex', alignItems: 'stretch', background: '#fff', borderRadius: 10 }}>
                  <div style={{
                    flex: 1, padding: '18px 20px',
                    fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                    fontSize: 22, fontWeight: 800, letterSpacing: 3,
                    color: '#0F1225', textAlign: 'center',
                    wordBreak: 'break-all',
                  }}>
                    {result.code}
                  </div>
                  <button onClick={copyCode} className="btn btn-primary" style={{ borderRadius: '0 8px 8px 0', fontSize: 13, minWidth: 100, boxShadow: 'none' }}>
                    {copied ? '✓ Copied!' : '📋 Copy'}
                  </button>
                </div>
              </div>
              {result.points_earned > 0 && (
                <div style={{ marginTop: 16, padding: '12px 16px', background: 'linear-gradient(135deg, #F3EEFF 0%, #FFE8D6 100%)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 20 }}>✨</span>
                  <p style={{ fontSize: 13, color: '#0F1225', fontWeight: 500 }}>
                    You earned <strong className="gradient-text" style={{ fontWeight: 800 }}>{result.points_earned} PerkVora points</strong>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* No-code (affiliate) state */}
          {result && !result.code && !loading && (
            <div style={{ marginBottom: 22 }}>
              <div style={{ padding: '18px 20px', background: 'linear-gradient(135deg, #F3EEFF 0%, #FFE8D6 100%)', borderRadius: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>🎉</div>
                <p style={{ fontSize: 15, fontWeight: 700, color: '#0F1225', marginBottom: 4 }}>Deal activated!</p>
                <p style={{ fontSize: 13, color: '#4A5568' }}>Your discount will be applied automatically at checkout.</p>
                {result.points_earned > 0 && (
                  <p style={{ fontSize: 13, color: '#0F1225', marginTop: 10 }}>
                    ✨ You earned <strong className="gradient-text" style={{ fontWeight: 800 }}>{result.points_earned} points</strong>
                  </p>
                )}
              </div>
            </div>
          )}

          {coupon.terms && (
            <details style={{ marginBottom: 18, background: '#F7F8FC', padding: '10px 14px', borderRadius: 8 }}>
              <summary style={{ fontSize: 12, color: '#4A5568', cursor: 'pointer', fontWeight: 600 }}>Terms & Conditions</summary>
              <p style={{ fontSize: 12, color: '#4A5568', marginTop: 8, lineHeight: 1.6 }}>{coupon.terms}</p>
            </details>
          )}

          {coupon.ftc_disclosure && (
            <p className="ftc-notice" style={{ marginBottom: 18, textAlign: 'center' }}>
              *Affiliate link — we may earn a commission at no extra cost to you.
            </p>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            {(coupon.merchant?.website || coupon.affiliate_tracking_url) && (
              <button onClick={handleVisitMerchant} className="btn btn-primary btn-lg" style={{ flex: 1 }}>
                Shop at {coupon.merchant?.name} →
              </button>
            )}
            <button onClick={handleClose} className="btn btn-secondary btn-lg" style={{ flex: coupon.merchant?.website ? 0 : 1 }}>
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
